




































































































































































import { Component, Vue, Watch } from "@/shared/vuepack";
import * as _ from "lodash";
import {
  CommunicationDeliveryType,
  CommunicationDeliveryTypeMap,
  ContactInformation,
  FetchState,
  NotificationPreference,
  NotificationPreferenceUpdateRequest,
  Preference,
  PreferenceConfiguration,
} from "@/shared/types";
import * as RouteNames from "@/employee/route-names";
import SettingsNavigation from "@/employee/components/settings/SettingsNavigation.vue";
import SettingsHeader from "@/employee/components/settings/SettingsHeader.vue";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import { useContactInfoStore } from "@/shared/store/contactinfo";
import { useThemeStore } from "@/shared/store/theme";
import { useNotificationPreferencesStore } from "@/shared/store/notificationPreferences";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useNotifierStore } from "@/shared/store/notifier";

const initialState: ContactInformation = {
  firstName: "",
  middleInitial: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  mobilePhoneNumber: "",
  isMailingSameAsPhysical: false,
  dateOfBirth: "",
  mailingAddress: {
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: ""
  },
  physicalAddress: {
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: ""
  },
  lastFourSsn: "",
  memberNumber: "",
  lastLogin: "",
  username: ""
};

@Component({
  computed: {
    CommunicationDeliveryTypeMap () {
      return CommunicationDeliveryTypeMap;
    }
  },
  components: {
    SettingsNavigation,
    SettingsHeader,
    GenericLoadingSpinner,
  }
})
export default class UserNotificationSettings extends Vue {
  RouteNames = RouteNames;
  notificationPreferencesStore = useNotificationPreferencesStore();
  themeStore = useThemeStore();
  contactInfoStore = useContactInfoStore();
  featureFlagStore = useFeatureFlagStore();
  notifierStore = useNotifierStore();
  formModel: ContactInformation = { ...initialState };
  updatedPreferences: NotificationPreference[] = [];
  communicationDeliveryTypeMap = new Map<string, Map<string, boolean>>();
  isOptedIntoPaper = false;
  debounceSaveNotificationPreferences = _.debounce(this.saveNotificationPreferences, 1000);
  disablePaperlessToggle = false;

  mounted () {
    this.contactInfoStore.getContactInformation();
    this.initializeFromStateValue();
    this.notificationPreferencesStore.getNotificationPreferences();
  }

  @Watch("notificationPreferencesStore.notificationPreferencesLoaded", {immediate: true})
  onNotificationPreferencesLoaded () {
    if (this.notificationPreferencesStore.notificationPreferencesLoaded) {
      this.initializePreferenceState();
    }
  }

  @Watch("notificationPreferencesStore.hasSavedNotificationPreferences", {immediate: true})
  onHasSavedNotificationPreferences () {
    if (this.notificationPreferencesStore.hasSavedNotificationPreferences) {
      this.initializePreferenceState();
      this.disablePaperlessToggle = false;
    }
  }

  initializePreferenceState () {
    this.createPreferenceMaps();
    this.isOptedIntoPaper = this.HasAnyPaperPreferences;
  }

  get isLoaded () {
    return this.contactInfoStore.contactInformationState === FetchState.Loaded;
  }

  get isNotificationPreferencesLoaded () {
    return this.notificationPreferencesStore.notificationPreferencesLoaded;
  }

  get isNotificationPreferencesLoading () {
    return this.notificationPreferencesStore.notificationPreferencesLoading;
  }

  get showNotificationPreferences () {
    return this.isNotificationPreferencesLoaded &&
        this.notificationPreferencesStore.notificationPreferences.length > 0;
  }

  get notificationPreferences (): NotificationPreference[] | [] {
    return this.notificationPreferencesStore.notificationPreferences;
  }

  get HasAnyPaperPreferences (): boolean {
    for (const notificationPreference of this.notificationPreferences) {
      for (const preference of notificationPreference.preferences) {
        if (preference.configurations.find(x =>
            x.communicationDeliveryType === CommunicationDeliveryType.Paper &&
            x.optedIn === true &&
            x.enabled === true)) {
          return true;
        }
      }
    }
    return false;
  }

  onTogglePaperlessCommunications () {
    if (this.isOptedIntoPaper === true) {
      this.disablePaperlessToggle = true;
      this.addPaperlessNotificationPreferences();
      this.saveNotificationPreferences();
    }
  }

  addPaperlessNotificationPreferences () {
    this.debounceSaveNotificationPreferences.cancel();
    for (const notificationPreference of this.notificationPreferences) {
      for (const [prefIndex, preference] of notificationPreference.preferences.entries()) {
        for (const configuration of preference.configurations) {
          if (configuration.communicationDeliveryType === CommunicationDeliveryType.Paper &&
              configuration.optedIn === true &&
              configuration.enabled === true) {
            configuration.optedIn = false;
            const matchingPreference = notificationPreference.preferences[prefIndex];
            for (const matchingConfiguration of matchingPreference.configurations) {
              if (matchingConfiguration.communicationDeliveryType === CommunicationDeliveryType.Email &&
                  matchingConfiguration.optedIn === false &&
                  matchingConfiguration.enabled === true) {
                matchingConfiguration.optedIn = true;
              }
            }
          }
        }
      }
    }
  }

  getThresholdTitle (preference: Preference, communicationDeliveryTypeKey: string): string {
    if (this.showToggleTooltip(preference, communicationDeliveryTypeKey) &&
        this.isCommunicationDeliveryTypeDisabled(preference, communicationDeliveryTypeKey)) {
      return "Amount required in order to enable this notification.";
    }
    return "";
  }

  showToggleTooltip (preference: Preference, communicationDeliveryTypeKey: string): boolean {
    if (preference.threshold) {
      return communicationDeliveryTypeKey === CommunicationDeliveryType.Sms ||
          communicationDeliveryTypeKey === CommunicationDeliveryType.Email;
    }
    return false;
  }

  onToggleConfiguration (notification: NotificationPreference, preference: Preference, communicationDeliveryTypeKey: string, newValue: boolean) {
    if (!notification && !preference) {
      return;
    }
    const config = this.getConfig(preference.configurations, communicationDeliveryTypeKey);
    if (!config) {
      return;
    }
    config.optedIn = newValue;

    this.debounceSaveNotificationPreferences();
  }

  getConfig (configurations: PreferenceConfiguration[], communicationDeliveryType: string): PreferenceConfiguration | null {
    if (configurations && configurations.length > 0) {
      return configurations.find(c => c.communicationDeliveryType === communicationDeliveryType);
    }
    return null;
  }

  createPreferenceMaps () {
    this.communicationDeliveryTypeMap.clear();
    for (const notificationPreference of this.notificationPreferencesStore.notificationPreferences) {
      for (const preference of notificationPreference.preferences) {
        const key = this.preferenceKey(notificationPreference.category, preference.notificationPreferenceType);
        const deliveryTypeMap = new Map<string, boolean>();
        for (const config of preference.configurations) {
          deliveryTypeMap.set(config.communicationDeliveryType, config.optedIn);
        }
        this.communicationDeliveryTypeMap.set(key, deliveryTypeMap);
      }
    }
  }

  preferenceKey (category: string, notificationPreferenceType: string) {
    return `${category}::${notificationPreferenceType}`;
  }

  async saveNotificationPreferences () {
    this.AddChangedNotificationPreferences();

    if (this.updatedPreferences.length === 0) {
      return;
    }
    const request: NotificationPreferenceUpdateRequest = {
      notificationPreferences: this.updatedPreferences
    };
    await this.notificationPreferencesStore.updateNotificationPreferences(request);
    this.createPreferenceMaps();
    this.updatedPreferences = [];
  }

  cloneToCurrentModel (o) {
    return _.cloneDeep(o);
  }

  isThresholdDisabled (preference: Preference): boolean {
    const smsDeliveryType = preference.configurations.find(x => x.communicationDeliveryType === CommunicationDeliveryType.Sms);
    const emailDeliveryType = preference.configurations.find(x => x.communicationDeliveryType === CommunicationDeliveryType.Email);
    return !smsDeliveryType.enabled && !emailDeliveryType.enabled;
  }

  get thresholdAmountValidator () {
    return {
      required: true,
      decimal: 2,
      min_value_usd: 0.01,
    };
  }

  hasThresholdOption (preference: Preference): boolean {
    return this.notificationPreferencesStore.hasThresholdOption(preference);
  }

  isCommunicationDeliveryTypeDisabled (preference: Preference, index: string): boolean {
    const config = this.getConfig(preference.configurations, index);
    if (!config.enabled) {
      return true;
    }
    if ((config.communicationDeliveryType === CommunicationDeliveryType.Sms ||
            config.communicationDeliveryType === CommunicationDeliveryType.Email) &&
        this.notificationPreferencesStore.hasThresholdOption(preference) && (preference.threshold.value <= 0 || !preference.threshold)) {
      return true;
    }
  }

  onThresholdChanged (preference: Preference, notification: NotificationPreference) {
    const hasPaperlessCommunications = preference.configurations
        .some(x => x.communicationDeliveryType === CommunicationDeliveryType.Email && x.optedIn === true
            || (x.communicationDeliveryType === CommunicationDeliveryType.Sms && x.optedIn === true));
    if (hasPaperlessCommunications) {
      this.debounceSaveNotificationPreferences();
    }
  }

  hasPaperlessCommunications (preference: Preference): boolean {
    const hasEmailOn = preference.configurations
        .find(x => x.communicationDeliveryType === CommunicationDeliveryType.Email &&
            x.enabled && x.optedIn);
    const hasSMSOn = preference.configurations
        .find(x => x.communicationDeliveryType === CommunicationDeliveryType.Sms &&
            x.enabled && x.optedIn);
    return !!(hasEmailOn || hasSMSOn);
  }

  AddChangedNotificationPreferences () {
    for (const notification of this.notificationPreferences) {
      const existingNotificationIndex = this.updatedPreferences.findIndex(
          n => n.category === notification.category
      );

      for (const preference of notification.preferences) {
        const snapshotNotification =
            this.notificationPreferencesStore.notificationPreferencesSnapshot.find(
                x => x.category === notification.category
            );

        const snapshotPreference = snapshotNotification?.preferences.find(
            x =>
                x.notificationPreferenceType ===
                preference.notificationPreferenceType
        );

        const hasPreferenceChanged = !_.isEqual(preference, snapshotPreference);
        if (!hasPreferenceChanged) {
          continue;
        }

        if (preference.threshold && preference.threshold.value > 0) {
          if (!this.hasPaperlessCommunications(preference)) {
            continue;
          }
        }

        const thresholdCopy = preference.threshold
            ? {value: preference.threshold.value}
            : null;

        const changedPreference = {
          notificationPreferenceType: preference.notificationPreferenceType,
          name: preference.name,
          configurations: preference.configurations.map(c => ({
            communicationDeliveryType: c.communicationDeliveryType,
            enabled: c.enabled,
            optedIn: c.optedIn
          })),
          threshold: thresholdCopy
        };

        if (existingNotificationIndex === -1) {
          this.updatedPreferences.push({
            category: notification.category,
            categoryDescription: notification.categoryDescription,
            preferences: [changedPreference]
          });
        } else {
          const existingNotification = this.updatedPreferences[existingNotificationIndex];
          const existingPreferenceIndex =
              existingNotification.preferences.findIndex(
                  x =>
                      x.notificationPreferenceType ===
                      preference.notificationPreferenceType
              );

          if (existingPreferenceIndex === -1) {
            existingNotification.preferences.push(changedPreference);
          } else {
            existingNotification.preferences.splice(
                existingPreferenceIndex,
                1,
                changedPreference
            );
          }
        }
      }
    }
  }

  getThresholdValidationField (category: string, index: number) {
    return `threshold-${category}-${index}`;
  }

  getThresholdValidationRules (preference: Preference) {
    if (!this.notificationPreferencesStore.hasThresholdOption(preference) || this.isThresholdDisabled(preference)) {
      return "";
    } else {
      return this.thresholdAmountValidator;
    }
  }

  @Watch("contactInfoStore.contactInformationState")
  initializeFromStateValue () {
    const contactInformation = this.contactInfoStore.contactInformation;
    if (this.contactInfoStore.contactInformationState === FetchState.Loaded && !contactInformation) {
      this.formModel = this.cloneToCurrentModel(initialState);
    } else if (contactInformation) {
      this.formModel = this.cloneToCurrentModel(contactInformation);
    }
  }

  @Watch("contactInfoStore.contactInformation")
  onPlanStateChanged () {
    this.initializeFromStateValue();
  }
  get showUndeliverableAddressWarning () {
    return this.formModel.mailingAddress.isUndeliverable === true;
  }
  get privacyUrl () {
    return "https://hsabank.com/HSABank/About/Security/Privacy-and-Opt-Out-Notice";
  }

}
